/**
 * Results for one output.
 *
 * The reporting rules from PRD §4.7 are enforced here, not left to the reader:
 * a defect rate the sample size cannot resolve is shown as a bound rather than
 * a point estimate, and Cp/Cpk carry a visible caveat whenever the output fails
 * a normality test. A confidently wrong number is worse than an honest range.
 */

import { useRef } from 'react';
import type { FullAnalysis } from '../../engine/analysis.ts';
import type { Project } from '../../engine/model.ts';
import { Histogram } from '../charts/Histogram.tsx';
import { Pareto } from '../charts/Pareto.tsx';
import { MethodCompare } from '../charts/MethodCompare.tsx';
import { TolerancePanel } from './TolerancePanel.tsx';
import { exportChartSvg, exportCsv } from '../export.ts';
import { eng, percent, ppm, sig, count } from '../format.ts';
import { Info } from '../Info.tsx';
import type { TopicId } from '../glossary.tsx';

interface Props {
  analysis: FullAnalysis;
  outputIndex: number;
  /** Present when the live project is available, enabling the tolerance probe. */
  project?: Project;
}

/** Wraps a chart with an SVG export affordance. */
function Exportable({ name, children }: { name: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div className="exportable" ref={ref}>
      {children}
      <button
        type="button"
        className="export-btn"
        onClick={() => exportChartSvg(ref.current, name)}
        title="Download this chart as SVG"
      >
        SVG
      </button>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
  topic,
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  tone?: 'good' | 'warn' | 'bad';
  /** Attaches a `?` marker explaining what this figure is. */
  topic?: TopicId;
}) {
  return (
    <div className={`stat${tone ? ` stat-${tone}` : ''}`}>
      <span className="stat-label">
        {label}
        {topic && <Info topic={topic} />}
      </span>
      <span className="stat-value">{value}</span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

export function ResultsPanel({ analysis, outputIndex, project }: Props) {
  const result = analysis.monteCarlo.outputs[outputIndex];
  const comparison = analysis.comparisons[outputIndex];

  // A contributor's half range is in the *variable's* unit, not the output's —
  // ∂out/∂var carries the conversion. Labelling it with the output unit would
  // read as though a resistor tolerance were measured in volts.
  const varUnit = (id: string) => project?.variables.find((v) => v.id === id)?.unit;
  const { stats, capability: cap, normality } = result;
  const y = result.yield;

  const yieldTone = y ? (y.point >= 0.999 ? 'good' : y.point >= 0.99 ? 'warn' : 'bad') : undefined;

  return (
    <div className="results">
      <div className="stat-row">
        <Stat label="Mean" value={eng(stats.mean, 5, result.unit)} />
        <Stat
          label="Std deviation"
          topic="sigma"
          value={eng(stats.stdDev, 4, result.unit)}
          sub={`±3σ = ${eng(3 * stats.stdDev, 3, result.unit)}`}
        />
        <Stat
          label="Observed range"
          value={`${eng(stats.min, 4, result.unit)} … ${eng(stats.max, 4, result.unit)}`}
          sub={`over ${count(stats.count)} samples`}
        />

        {y && (
          <Stat
            label="Yield"
            topic="yield"
            value={y.belowResolution && y.failures === 0 ? `> ${percent(y.lower, 4)}` : percent(y.point, 4)}
            tone={yieldTone}
            sub={
              <>
                95% CI {percent(y.lower, 4)} – {percent(y.upper, 4)}
              </>
            }
          />
        )}

        {y && (
          <Stat
            label="Defect rate"
            topic="ppm"
            value={
              y.belowResolution
                ? `< ${ppm(y.ppmUpper)} PPM`
                : `${ppm(y.ppm)} PPM`
            }
            tone={y.belowResolution ? undefined : yieldTone}
            sub={
              /* Below resolution, the range is the answer — the point estimate
                 does not exist. Carrying it here rather than in a callout keeps
                 one fact in one place, next to the number it qualifies. */
              y.belowResolution ? (
                <span className="bad">
                  {y.failures === 0
                    ? `no failures observed — true rate below ${ppm(y.ppmUpper)} PPM`
                    : `only ${count(y.failures)} failure${y.failures === 1 ? '' : 's'} observed ` +
                      `— true rate ${ppm(y.ppmLower)} to ${ppm(y.ppmUpper)} PPM`}
                </span>
              ) : (
                <>
                  CI {ppm(y.ppmLower)} – {ppm(y.ppmUpper)} PPM
                </>
              )
            }
          />
        )}

        {cap.cpk !== undefined && (
          <Stat
            label="Cpk"
            topic="cp-cpk"
            value={sig(cap.cpk, 3)}
            tone={cap.cpk >= 1.33 ? 'good' : cap.cpk >= 1.0 ? 'warn' : 'bad'}
            sub={
              /* `matters`, not `rejected`. At 10^5 samples `rejected` fires on
                 an output that is normal to three decimal places, and a caveat
                 attached to a sound Cpk is worse than none — it teaches the
                 reader to ignore the caveat on an unsound one. */
              normality.matters ? (
                <span className="bad">output is not normal — prefer the empirical yield</span>
              ) : cap.cp !== undefined ? (
                `Cp ${sig(cap.cp, 3)}`
              ) : undefined
            }
          />
        )}
      </div>

      {comparison.notes.map((note, i) => (
        <p key={i} className="callout warn">
          {note}
        </p>
      ))}

      {comparison.wca.warnings.map((w, i) => (
        <p key={`w${i}`} className="callout warn">
          <strong>Worst case:</strong> {w}
        </p>
      ))}

      <Exportable name={`${result.id}-distribution`}>
        <Histogram result={result} />
      </Exportable>

      <Exportable name={`${result.id}-methods`}>
        <MethodCompare comparison={comparison} result={result} />
      </Exportable>
      {/* Facts about how worst case was computed, beside the bar they explain
          rather than stacked with findings about the model itself. */}
      {comparison.wca.notes.map((n, i) => (
        <p key={`n${i}`} className="chart-note muted">
          {n}
        </p>
      ))}

      <div className="method-table-wrap">
        <table className="data-table">
          <caption>
            Worst-case contributors — {result.id} <Info topic="worst-case" />
            <button
              type="button"
              className="link-btn"
              onClick={() =>
                exportCsv(
                  [
                    ['variable', 'partial', 'half_range', 'contribution', 'share', 'extreme_at'],
                    ...comparison.wca.contributions
                      .filter((c) => c.contribution > 0)
                      .map((c) => [
                        c.varId,
                        c.partial,
                        c.halfRange,
                        c.contribution,
                        c.fraction,
                        c.cornerForMax === 'max' ? 'upper limit' : 'lower limit',
                      ]),
                  ],
                  `${result.id}-contributors`,
                )
              }
            >
              Export CSV
            </button>
          </caption>
          <thead>
            <tr>
              <th scope="col">Variable</th>
              <th scope="col">∂out/∂var</th>
              <th scope="col">Half range</th>
              <th scope="col">Contribution</th>
              <th scope="col">Share</th>
              <th scope="col">Extreme at</th>
            </tr>
          </thead>
          <tbody>
            {comparison.wca.contributions
              .filter((c) => c.contribution > 0)
              .map((c) => (
                <tr key={c.varId}>
                  <td>
                    <code>{c.varId}</code>
                    {c.clamped && <span className="tag small">3σ clamp</span>}
                  </td>
                  <td>{sig(c.partial, 4)}</td>
                  <td>{eng(c.halfRange, 3, varUnit(c.varId))}</td>
                  <td>{eng(c.contribution, 3, result.unit)}</td>
                  <td>{percent(c.fraction, 1)}</td>
                  <td>{c.cornerForMax === 'max' ? 'upper limit' : 'lower limit'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Pareto result={result} />

      {project && (
        <TolerancePanel project={project} outputIndex={outputIndex} unit={result.unit} />
      )}
    </div>
  );
}
